/* eslint-disable no-nested-ternary */
/* eslint-disable jsx-a11y/label-has-associated-control */
import { Controller, FormProvider, useForm } from 'react-hook-form';
import { useEffect, useState } from 'react';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useAtomValue } from 'jotai';
import { MdCheck } from 'react-icons/md';
import Button from '@components/NewButton/Button';
import {
  Modal,
  ModalContainer,
  ModalContent,
  ModalFooter,
  ModalHeader,
} from '@components/Modal';
import {
  BuildingLibraryIcon,
  CheckCircleIcon,
} from '@heroicons/react/24/outline';
import { Autocomplete } from '../../tailwindComponents/Autocomplete';
import {
  AUTHORISE_BANK_ACCOUNT,
  GET_BANK_DETAILS,
  GET_SUPPORTED_BANKS,
  REVOKE_BANK_ACCOUNT_CONSENT,
} from '../../constants';
import { useAuthHttp } from '../../hooks';
import { businessProfileAtom } from '../../store/jotai';

const schema = yup.object().shape({
  bank: yup
    .object()
    .shape({
      bankName: yup.string().required('Bank name is required'),
      // branchCode: yup.string().required('Branch code is required'),
      bankInstitutionId: yup
        .string()
        .required('Bank institution ID is required'),
    })
    .required(),
});

type BankAccount = {
  bankName: string;
  branchCode: string;
  bankInstitutionId: string;
};

export default function FinanceForm({
  hasConnectedBankAccount,
  isShipper,
}: {
  hasConnectedBankAccount: boolean;
  isShipper: boolean;
}) {
  const authHttp = useAuthHttp();
  const businessProfile = useAtomValue(businessProfileAtom);
  const [isFormSubmitting, setIsFormSubmitting] = useState(false);
  const [supportedBanks, setSupportedBanks] = useState<
    { id: string; label: string; bankInstitutionId: string }[]
  >([]);
  const [isBankListUnavailable, setIsBankListUnavailable] = useState(false);
  const [bankAccount, setBankAccount] = useState<BankAccount | null>(null);
  const [isConsentModalOpen, setIsConsentModalOpen] = useState(false);
  const [
    isRevokeBankAccountConsentModalOpen,
    setIsRevokeBankAccountConsentModalOpen,
  ] = useState(false);
  const [isAuthorisingBankAccount, setIsAuthorisingBankAccount] =
    useState(false);
  const [isRevoking, setIsRevoking] = useState(false);
  const [bankSearchValue, setBankSearchValue] = useState('');

  const defaultValues = {
    bank: {
      bankName: 'Select a bank',
      branchCode: '',
      bankInstitutionId: '',
    },
  };

  const methods = useForm({
    shouldUnregister: false,
    resolver: yupResolver(schema),
    mode: 'onChange',
    defaultValues: { ...defaultValues },
  });

  const {
    reset,
    control,
    handleSubmit,
    formState: { errors },
    getValues,
  } = methods;

  const onSubmit = async () => {
    setIsConsentModalOpen(true);
  };

  const handleAuthoriseBankAccount = async () => {
    setIsAuthorisingBankAccount(true);
    try {
      const { data } = await authHttp.post(
        AUTHORISE_BANK_ACCOUNT,
        {
          baseUrl: window.location.origin,
          bankInstitutionId: getValues().bank.bankInstitutionId,
          redirectUrl: 'profile/update-financial-information',
        },
        { headers: { 'Content-Type': 'application/json' } }
      );
      if (data.bankAccountAuthorisationUrl) {
        window.location.href = data.bankAccountAuthorisationUrl;
      }
    } catch (error) {
      console.error(error);
      setIsAuthorisingBankAccount(false);
    }
  };

  const fetchBankDetails = async () => {
    try {
      const { data } = await authHttp.get(GET_BANK_DETAILS);

      if (data.bankAccount) {
        setBankAccount(data.bankAccount);
        setBankSearchValue(data.bankAccount.bankName);
        reset({
          bank: {
            bankName: data.bankAccount.bankName,
            branchCode: data.bankAccount.branchCode,
            bankInstitutionId: data.bankAccount.bankInstitutionId,
          },
        });
      }
    } catch (error) {
      console.error(error);
    }
  };

  const fetchSupportedBanks = async () => {
    try {
      const { data } = await authHttp.get(GET_SUPPORTED_BANKS);
      setIsBankListUnavailable(false);

      if (data) {
        setSupportedBanks(
          data.supportedBanks.map((bank: any) => ({
            id: bank.id,
            label: bank.name,
            bankInstitutionId: bank.id,
          }))
        );
      }
    } catch (error) {
      console.error(error);
      setIsBankListUnavailable(true);
    }
  };

  const handleRevokeConsent = async () => {
    setIsRevoking(true);
    try {
      const { data } = await authHttp.post(
        REVOKE_BANK_ACCOUNT_CONSENT,
        {},
        { headers: { 'Content-Type': 'application/json' } }
      );
      if (data) {
        setBankAccount(data.bankAccount);
        setIsRevokeBankAccountConsentModalOpen(false);
      }
    } catch (error) {
      console.error(error);
    } finally {
      setIsRevoking(false);
    }
  };

  const getDatePlus90Days = () => {
    const today = new Date();
    today.setDate(today.getDate() + 90);
    return today.toLocaleDateString();
  };

  useEffect(() => {
    if (!businessProfile) return;
    fetchBankDetails();
    fetchSupportedBanks();
  }, [businessProfile]);

  return (
    <>
      <div className='bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden'>
        <div className='border-b border-gray-200 bg-gray-50 px-6 py-4'>
          <div className='flex items-center gap-3'>
            <BuildingLibraryIcon className='h-5 w-5 text-gray-400' />
            <h2 className='text-lg font-semibold text-gray-900'>
              Bank Details
            </h2>
          </div>
        </div>
        <div className='p-6'>
          <p className='text-sm text-gray-600 mb-6'>
            {isShipper
              ? 'To help us verify your company and facilitate payment flows for the logistics services you receive through the FLOX platform, we require access to limited bank account information.'
              : 'To help us verify you as a service provider and enable payments into your bank account for services delivered through the FLOX platform, we require access to limited bank account information.'}
          </p>

          <FormProvider {...methods}>
            <form
              onSubmit={handleSubmit(onSubmit)}
              noValidate
              className='space-y-6'
            >
              <Controller
                name='bank'
                control={control}
                render={({ field }) => (
                  <Autocomplete
                    label='Bank'
                    options={supportedBanks.filter((bank) =>
                      (bank.label ?? '')
                        .toLowerCase()
                        .includes((bankSearchValue ?? '').toLowerCase())
                    )}
                    value={bankSearchValue}
                    onChange={(option) => {
                      if (!option) return;
                      field.onChange({
                        bankName: option.label,
                        branchCode: '',
                        bankInstitutionId: option.bankInstitutionId,
                      });
                      setBankSearchValue(option.label);
                    }}
                    onInputChange={(value) => setBankSearchValue(value)}
                    placeholder='Search for your bank...'
                    isInvalid={!!errors.bank}
                    validationMessage={
                      errors.bank?.bankInstitutionId?.message ||
                      errors.bank?.bankName?.message
                    }
                    helperText={
                      isBankListUnavailable
                        ? 'Bank connections are temporarily unavailable.'
                        : ''
                    }
                    noOptionsText='No banks found'
                  />
                )}
              />

              <div className='flex flex-col sm:flex-row gap-3'>
                <Button
                  type='submit'
                  icon={
                    !methods.formState.isDirty && bankAccount ? (
                      <MdCheck size='1.25rem' />
                    ) : undefined
                  }
                  disabled={isFormSubmitting}
                  size='sm'
                  intent={
                    !bankAccount || methods.formState.isDirty
                      ? 'primary'
                      : 'success'
                  }
                  label={
                    isFormSubmitting
                      ? 'Connecting...'
                      : !bankAccount
                      ? 'Connect Bank Account'
                      : !methods.formState.isDirty
                      ? 'Verified'
                      : 'Update'
                  }
                />
                <Button
                  size='sm'
                  intent='secondary'
                  onClick={() => setIsRevokeBankAccountConsentModalOpen(true)}
                  disabled={!hasConnectedBankAccount || !bankAccount}
                  label='Revoke Consent'
                />
              </div>
            </form>
          </FormProvider>
        </div>
      </div>
      <Modal
        open={isConsentModalOpen}
        onOpenChange={(isOpen: boolean) => {
          setIsConsentModalOpen(isOpen);
        }}
      >
        <ModalContainer preventCloseOnOutsideClick>
          <ModalHeader title='Account Information Request' />
          <ModalContent>
            {isShipper && (
              <div className='space-y-4'>
                <p>
                  The information will be used for company vetting, payment
                  confirmation, and transaction reconciliation purposes. FLOX
                  will not process any payments from the connected bank account
                  without the explicit approval of your authorised company
                  personnel.
                </p>
                <p>
                  To enable the service, we have partnered with Yapily Connect
                  to access your bank data at {getValues().bank?.bankName} and
                  act as their agent when we share this information back to you.
                </p>
                <p>
                  You will now be securely redirected to{' '}
                  {getValues().bank?.bankName} to give access to the following
                  information:
                  <ul>
                    <li>Account(s) details</li>
                    <li>Balances</li>
                    <li>Transaction history</li>
                  </ul>
                </p>
                <p>
                  By using the service, you agree to Yapily Connect accessing
                  your bank data, the
                  <a
                    href='https://www.yapily.com/legal/end-user-terms?_gl=1*6m3mg2*_gcl_au*NzU2MzY1NTYxLjE3MzE0OTQ1MTg.'
                    target='_blank'
                    rel='noopener noreferrer'
                    className='text-blue-500 visited:text-purple-500'
                  >
                    &nbsp;Terms & Conditions&nbsp;
                  </a>
                  and
                  <a
                    href='https://www.yapily.com/legal/privacy-policy?_gl=1*6m3mg2*_gcl_au*NzU2MzY1NTYxLjE3MzE0OTQ1MTg.'
                    target='_blank'
                    rel='noopener noreferrer'
                    className='text-blue-500 visited:text-purple-500'
                  >
                    &nbsp;Privacy Notice
                  </a>
                  .
                </p>
                <p>
                  This consent will automatically expire on{' '}
                  {getDatePlus90Days()}.
                </p>
              </div>
            )}
            {!isShipper && (
              <div className='space-y-4'>
                <p>
                  FLOX will not collect any payments from the connected bank
                  account and will only use the information for service provider
                  vetting, payment confirmation, and transaction reconciliation
                  purposes.
                </p>
                <p>
                  To enable the service, we have partnered with Yapily Connect
                  to access your bank data at {getValues().bank?.bankName} and
                  act as their agent when we share this information back to you.
                </p>
                <p>
                  You will now be securely redirected to{' '}
                  {getValues().bank?.bankName} to give access to the following
                  information:
                  <ul>
                    <li>Account(s) details</li>
                    <li>Balances</li>
                    <li>Transaction history</li>
                  </ul>
                </p>
                <p>
                  By using the service, you agree to Yapily Connect accessing
                  your bank data, the
                  <a
                    href='https://www.yapily.com/legal/end-user-terms?_gl=1*6m3mg2*_gcl_au*NzU2MzY1NTYxLjE3MzE0OTQ1MTg.'
                    target='_blank'
                    rel='noopener noreferrer'
                    className='text-blue-500 visited:text-purple-500'
                  >
                    &nbsp;Terms & Conditions&nbsp;
                  </a>
                  and
                  <a
                    href='https://www.yapily.com/legal/privacy-policy?_gl=1*6m3mg2*_gcl_au*NzU2MzY1NTYxLjE3MzE0OTQ1MTg.'
                    target='_blank'
                    rel='noopener noreferrer'
                    className='text-blue-500 visited:text-purple-500'
                  >
                    &nbsp;Privacy Notice
                  </a>
                  .
                </p>
                <p>
                  This consent will automatically expire on{' '}
                  {getDatePlus90Days()}.
                </p>
              </div>
            )}
          </ModalContent>
          <ModalFooter>
            <Button
              intent='danger'
              size='lg'
              onClick={() => setIsConsentModalOpen(false)}
              label='Cancel'
            />
            <Button
              intent='primary'
              size='lg'
              onClick={handleAuthoriseBankAccount}
              disabled={isAuthorisingBankAccount}
              label={
                isFormSubmitting
                  ? 'Submitting...'
                  : isAuthorisingBankAccount
                  ? 'Authorising'
                  : 'Connect Bank Account'
              }
            />
          </ModalFooter>
        </ModalContainer>
      </Modal>
      <Modal
        open={isRevokeBankAccountConsentModalOpen}
        onOpenChange={(isOpen: boolean) => {
          setIsRevokeBankAccountConsentModalOpen(isOpen);
        }}
      >
        <ModalContainer preventCloseOnOutsideClick>
          <ModalHeader title='Withdrawal of Consent for Payment Processing' />
          <ModalContent>
            <div className='space-y-4'>
              <p>
                Are you sure you want to revoke consent for{' '}
                {bankAccount?.bankName}?
              </p>
              <p>
                We acknowledge your right to withdraw your consent for our
                payment processing platform* at any time. Please note that
                without your consent, we will be unable to process payments
                against your invoices and authorise subsequent shipments. Please
                confirm, if you wish to proceed with withdrawing your consent:
              </p>
              <p className='text-sm'>
                Note: *) Yapily Connect Ltd, Authorised Payment Institution, FCA
                Registered 827001.
              </p>
            </div>
          </ModalContent>
          <ModalFooter>
            <Button
              intent='primary'
              size='lg'
              onClick={() => setIsRevokeBankAccountConsentModalOpen(false)}
              label='Cancel'
            />
            <Button
              intent='danger'
              size='lg'
              onClick={handleRevokeConsent}
              label={isRevoking ? 'Revoking...' : 'Revoke Consent'}
            />
          </ModalFooter>
        </ModalContainer>
      </Modal>
    </>
  );
}
